import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

// Closed panel: a cheap count. Open thread: an incremental fetch. Both pause
// on a hidden tab, so a backgrounded window costs nothing.
const IDLE_POLL_MS = 30000;
const ACTIVE_POLL_MS = 5000;

export default function useMessaging({ enabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // The newest message timestamp we hold, so the active poll asks only for
  // what it does not already have.
  const cursor = useRef(null);

  // Which thread is actually active right now, tracked outside React state so
  // an in-flight pullMessages() can check it synchronously the instant its
  // response lands (state updates are async and would still show the old
  // thread mid-flight). Without this, a slow fetch for thread A that
  // resolves after the user has already switched to thread B would merge
  // A's messages into B's view and rewind the cursor to A's position.
  const activeThreadIdRef = useRef(null);

  // Flips false on unmount so a request already in flight cannot call
  // setState afterward. Checked after every await, not just once at the top
  // of a function, since state can change during any of the awaits.
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const refreshUnread = useCallback(async () => {
    const { data } = await api.get("/messages/unread-count");
    if (!mountedRef.current) return;
    setUnread(data.unread);
  }, []);

  const refreshThreads = useCallback(async () => {
    const { data } = await api.get("/messages/threads");
    if (!mountedRef.current) return;
    setThreads(data);
  }, []);

  const refreshContacts = useCallback(async () => {
    const { data } = await api.get("/messages/contacts");
    if (!mountedRef.current) return;
    setContacts(data);
  }, []);

  // Returns whether it actually applied any rows, so the active poll (below)
  // can skip the /read write when nothing arrived.
  const pullMessages = useCallback(async (threadId) => {
    const params = cursor.current ? { after: cursor.current } : undefined;
    const { data } = await api.get(`/messages/threads/${threadId}/messages`, { params });
    if (!mountedRef.current) return false;
    // Discard a stale response: the active thread may have changed while
    // this request was in flight. Without this check the response would be
    // merged into whichever thread is active now, and the cursor would be
    // rewound to the wrong thread's position.
    if (threadId !== activeThreadIdRef.current) return false;
    if (!data.length) return false;
    cursor.current = data[data.length - 1].created_at;
    setMessages((held) => {
      // A re-delivered message (e.g. one just voided) must replace the
      // previously held copy rather than being discarded as a duplicate -
      // otherwise a withdrawal fetched by this same poll would never be
      // reflected in the UI, since the id is already known.
      const incoming = new Map(data.map((m) => [m.id, m]));
      const merged = held.map((m) => incoming.get(m.id) ?? m);
      const newOnes = data.filter((m) => !held.some((h) => h.id === m.id));
      return [...merged, ...newOnes];
    });
    return true;
  }, []);

  const selectThread = useCallback(async (thread) => {
    if (!enabled) return;
    activeThreadIdRef.current = thread.id;
    setActiveThread(thread);
    setMessages([]);
    setError(null);
    cursor.current = null;
    try {
      await pullMessages(thread.id);
      // Bail if the user moved to a different thread (or the panel closed)
      // while pullMessages() was in flight; the read receipt and refreshes
      // below belong to `thread`, not to whatever is active now.
      if (!mountedRef.current || activeThreadIdRef.current !== thread.id) return;
      await api.post(`/messages/threads/${thread.id}/read`);
      if (!mountedRef.current || activeThreadIdRef.current !== thread.id) return;
      await Promise.all([refreshUnread(), refreshThreads()]);
    } catch {
      // A poll failure is not worth interrupting the user; the interceptor
      // handles an expired session.
    }
  }, [enabled, pullMessages, refreshUnread, refreshThreads]);

  const openThreadWith = useCallback(async (recipientId) => {
    if (!enabled) return;
    const { data } = await api.post("/messages/threads", { recipient_id: recipientId });
    if (!mountedRef.current) return;
    await refreshThreads();
    // `POST /messages/threads` returns the raw thread document, which has no
    // `other` field (only `GET /messages/threads` rows do). Without this the
    // panel header would read "Former member" - the placeholder reserved for
    // a revoked account - for every freshly started conversation.
    const contact = contacts.find((c) => c.id === recipientId);
    await selectThread({ ...data, other: contact });
  }, [enabled, contacts, refreshThreads, selectThread]);

  const closeThread = useCallback(() => {
    activeThreadIdRef.current = null;
    setActiveThread(null);
    setMessages([]);
    cursor.current = null;
  }, []);

  const openPanel = useCallback(() => {
    if (!enabled) return;
    setIsOpen(true);
    refreshThreads().catch(() => {});
    refreshContacts().catch(() => {});
  }, [enabled, refreshThreads, refreshContacts]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    closeThread();
  }, [closeThread]);

  const send = useCallback(async (body) => {
    // No active thread means there is nowhere to post the message (and
    // nothing in the UI should be able to call send() in that state); bail
    // rather than hit an endpoint with a missing thread id.
    if (!enabled || !activeThread) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/messages/threads/${activeThread.id}/messages`, { body });
      await pullMessages(activeThread.id);
      await refreshThreads();
    } catch (err) {
      // Surface it and leave the composer populated — text must never be lost
      // to a network blip.
      if (mountedRef.current) setError(formatApiError(err?.response?.data?.detail));
      throw err;
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [enabled, activeThread, pullMessages, refreshThreads]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState === "hidden") return;
      try {
        if (activeThread) {
          // Only write the read receipt when the poll actually pulled new
          // rows: otherwise every 5s tick on an idle open thread writes the
          // thread document for nothing - up to 12 writes/minute per open
          // panel whether or not anything arrived.
          const gotNewMessages = await pullMessages(activeThread.id);
          if (cancelled) return;
          if (gotNewMessages) {
            await api.post(`/messages/threads/${activeThread.id}/read`);
            if (cancelled) return;
          }
        }
        await refreshUnread();
        if (cancelled) return;
        if (isOpen) await refreshThreads();
      } catch {
        // Silent, matching the existing notification poll.
      }
    };

    tick();
    const timer = setInterval(tick, activeThread ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, isOpen, activeThread, pullMessages, refreshUnread, refreshThreads]);

  return {
    isOpen, openPanel, closePanel,
    unread, threads, contacts,
    activeThread, messages, selectThread, openThreadWith, closeThread,
    send, sending, error,
  };
}
